import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GoogleLogin } from "@react-oauth/google";
import "./auth.scss";

const AuthModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const [step, setStep] = useState(1); // 1: Phone/Google, 2: OTP, 3: Name
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setPhone("");
      setOtp("");
      setName("");
      setLoading(false);
    }
  }, [isOpen]);

  // --- LOGIC: GOOGLE LOGIN ---
  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    try {
      const res = await fetch("https://api.zulluai.com/api/google-login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken: credentialResponse.credential }),
      });

      const data = await res.json();

      if (data.success) {
        // Option A: Save user details separately
        localStorage.setItem("token", data.token);
        localStorage.setItem("userEmail", data.user.email);
        localStorage.setItem("userName", data.user.name);

        // Success! Close modal and refresh app state
        onLoginSuccess();
      } else {
        alert(data.message || "Google Authentication failed.");
      }
    } catch (e) {
      console.error("Google Auth Error:", e);
      alert("Failed to connect to Google Auth.");
    } finally {
      setLoading(false);
    }
  };

  // --- LOGIC: SEND SMS VIA MTALKZ ---
  const sendWebOtp = async () => {
    setLoading(true);
    try {
      const response = await fetch("https://api.zulluai.com/api/send-web-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phoneNumber: "+91" + phone,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStep(2);
      } else {
        alert(data.msg || "Failed to send OTP.");
      }
    } catch (e) {
      console.error("Auth Error:", e);
      alert("Server error. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // --- LOGIC: VERIFY OTP & CHECK USER ---
  const verifyOtp = async () => {
    setLoading(true);
    try {
      const res = await fetch("https://api.zulluai.com/api/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phoneNumber: "+91" + phone,
          otp: otp,
        }),
      });

      if (res.status === 200) {
        const existRes = await fetch(
          `https://api.zulluai.com/api/check-user-exists?phoneNumber=+91${phone}`
        );
        const existData = await existRes.json();

        if (existData.success) {
          localStorage.setItem("phoneNumber", "+91" + phone);
          onLoginSuccess();
        } else {
          setStep(3);
        }
      } else {
        alert("Invalid OTP code.");
      }
    } catch (e) {
      alert("Verification failed.");
    } finally {
      setLoading(false);
    }
  };

  // --- LOGIC: SAVE PROFILE (STEP 3) ---
  const saveProfile = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        "https://api.zulluai.com/api/save-personal-info",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phoneNumber: "+91" + phone,
            name: name,
            countryCode: "+91",
            countryName: "India",
            registrationDevice: "Desktop",
          }),
        }
      );

      if (res.status === 200) {
        localStorage.setItem("phoneNumber", "+91" + phone);
        onLoginSuccess();
      }
    } catch (e) {
      alert("Error saving profile.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="auth-overlay">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="auth-card"
      >
        <button className="close-btn" onClick={onClose}>
          &times;
        </button>

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div
              key="s1"
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -20, opacity: 0 }}
              className="auth-step"
            >
              <h2>Welcome</h2>
              <p>Experience the future of video generation.</p>

              {/* GOOGLE LOGIN BUTTON */}
              <div className="google-auth-wrapper">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => alert("Google Login Failed")}
                  theme="filled_black"
                  shape="pill"
                  width="100%"
                />
              </div>

              <div className="auth-divider">
                <span>OR</span>
              </div>

              <div className="input-group">
                <span>+91</span>
                <input
                  type="tel"
                  placeholder="Mobile number"
                  maxLength={10}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              <button
                className="primary-btn"
                onClick={sendWebOtp}
                disabled={phone.length < 10 || loading}
              >
                {loading ? "SENDING..." : "CONTINUE"}
              </button>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="s2"
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -20, opacity: 0 }}
              className="auth-step"
            >
              <h2>Verification</h2>
              <p>Enter 6-digit code sent to +91 {phone}</p>
              <input
                type="text"
                className="otp-input"
                placeholder=""
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
              />
              <button
                className="primary-btn"
                onClick={verifyOtp}
                disabled={otp.length < 6 || loading}
              >
                {loading ? "VERIFYING..." : "VERIFY & PROCEED"}
              </button>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              key="s3"
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -20, opacity: 0 }}
              className="auth-step"
            >
              <h2>Identity</h2>
              <p>Define your creator alias.</p>
              <input
                type="text"
                className="name-input"
                placeholder="Full Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <button
                className="primary-btn"
                onClick={saveProfile}
                disabled={!name || loading}
              >
                {loading ? "LAUNCHING..." : "LAUNCH ZULLU AI"}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default AuthModal;
